// frontend/packages/business-console/src/services/inboxStream.js
//
// Streaming-fetch wrapper for the SSE endpoint at
// GET /api/v2/inbox/conversations/:id/events.
//
// We use fetch() with ReadableStream rather than the browser's EventSource
// API because EventSource can't set custom request headers — and the
// backend auth middleware reads the Bearer token exclusively from the
// Authorization header. fetch() lets us reuse the existing auth flow
// without exposing tokens in query params.
//
// Reconnect strategy: exponential backoff starting at 1s, capped at 30s.
// Each connect attempt re-fetches the auth token via the supplied
// getToken callback so token rotation Just Works.
//
// Returns an unsubscribe function. Cancellation aborts the underlying
// AbortController, which closes the stream both client- and server-side.

import { api } from "@upryzze/services";

function getApiBase() {
  if (api?.defaults?.baseURL) return api.defaults.baseURL;
  if (
    typeof window !== "undefined" &&
    window.__UPRYZZE_ENV__ &&
    window.__UPRYZZE_ENV__.REACT_APP_API_URL
  ) {
    return window.__UPRYZZE_ENV__.REACT_APP_API_URL;
  }
  // CRA-style env, available in webpack-built bundles even at runtime.
  // eslint-disable-next-line no-undef
  return (typeof process !== "undefined" && process.env && process.env.REACT_APP_API_URL) || "";
}

/**
 * Subscribe to a conversation's event stream.
 *
 * @param {string} conversationId
 * @param {object} opts
 * @param {function():Promise<*>=} opts.ensureSignedIn  Called before each
 *     connect attempt to refresh the auth token silently if it's near
 *     expiry. Optional — if missing, we just call getToken() directly.
 * @param {function():string} opts.getToken  Required. Returns the current
 *     Bearer token.
 * @param {string} opts.businessId  Required. Sent as `x-business-id` so the
 *     backend's business-access middleware can scope the request.
 * @param {function(object)} opts.onMessageAppended  Called with the parsed
 *     event payload when a new message lands on the conversation.
 * @param {function()=} opts.onConnect  Called once each time the underlying
 *     fetch returns 200 and the stream is open.
 * @param {function(Error)=} opts.onError  Called on connection errors
 *     (network, HTTP non-2xx). After firing, the wrapper reconnects with
 *     backoff — the caller doesn't need to do anything.
 *
 * @returns {function()} Unsubscribe. Idempotent.
 */
export function subscribeToConversationEvents(
  conversationId,
  {
    ensureSignedIn,
    getToken,
    businessId,
    onMessageAppended,
    onConnect,
    onError,
  } = {},
) {
  const controller = new AbortController();
  let cancelled = false;
  let retryDelayMs = 1000;
  const maxRetryDelayMs = 30000;

  const baseUrl = getApiBase();

  function parseFrame(raw) {
    if (!raw) return null;
    const out = { event: "", data: "", id: "" };
    for (const line of raw.split("\n")) {
      if (!line) continue;
      // SSE comments (used as keep-alive) start with `:`. Ignore them.
      if (line.startsWith(":")) continue;
      const colon = line.indexOf(":");
      if (colon === -1) continue;
      const field = line.slice(0, colon);
      let value = line.slice(colon + 1);
      if (value.startsWith(" ")) value = value.slice(1);
      if (field === "event") out.event = value;
      else if (field === "data") out.data = value;
      else if (field === "id") out.id = value;
    }
    return out;
  }

  function dispatch(frame) {
    if (!frame || !frame.event) return;
    let parsed;
    try {
      parsed = JSON.parse(frame.data);
    } catch {
      return;
    }
    if (frame.event === "message_appended") {
      if (typeof onMessageAppended === "function") {
        try {
          onMessageAppended(parsed);
        } catch (err) {
          if (typeof onError === "function") onError(err);
        }
      }
    }
    // Future event types (status_changed, customer_typing, presence, etc.)
    // dispatch through here. Adding a new event is one branch + one
    // optional callback on the opts object.
  }

  function sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  async function connect() {
    if (cancelled) return;
    try {
      if (typeof ensureSignedIn === "function") {
        try {
          await ensureSignedIn();
        } catch {
          // Token refresh failed; we still try with whatever token is
          // currently in storage. If it's expired the request returns
          // 401, errors out, and the backoff path triggers a retry that
          // calls ensureSignedIn again.
        }
      }

      const token = typeof getToken === "function" ? getToken() : "";
      if (!token) {
        throw new Error("No auth token available");
      }
      if (!businessId) {
        throw new Error("No active businessId available");
      }
      if (!baseUrl) {
        throw new Error("API base URL not resolved");
      }

      const url = `${baseUrl}/v2/inbox/conversations/${encodeURIComponent(conversationId)}/events`;
      // NOTE on request headers:
      //   - Authorization and x-business-id trigger a CORS preflight. The
      //     backend's dynamicCors middleware allowlists both.
      //   - Accept: text/event-stream is a CORS "simple" header so it
      //     doesn't require allowlisting.
      //   - Cache-Control would also trigger preflight, and the backend
      //     does NOT allowlist it. Sending it caused
      //     "Request header field cache-control is not allowed by
      //      Access-Control-Allow-Headers in preflight response"
      //     and the entire fetch was blocked. The header is redundant
      //     anyway — the server response sets
      //     Cache-Control: no-cache, no-transform on the SSE response,
      //     which prevents caching at the receiver. Do NOT re-add it.
      const res = await fetch(url, {
        method: "GET",
        signal: controller.signal,
        headers: {
          Authorization: `Bearer ${token}`,
          "x-business-id": String(businessId),
          Accept: "text/event-stream",
        },
      });
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
      if (typeof onConnect === "function") {
        try {
          onConnect();
        } catch {
          // ignore
        }
      }
      // Successful connect — reset the backoff window.
      retryDelayMs = 1000;

      const reader = res.body.getReader();
      const decoder = new TextDecoder("utf-8");
      let buffer = "";

      while (!cancelled) {
        let chunk;
        try {
          chunk = await reader.read();
        } catch (err) {
          // Aborted reads bubble here when the controller fires; treat as
          // a clean shutdown.
          if (controller.signal.aborted) return;
          throw err;
        }
        if (chunk.done) break;
        buffer += decoder.decode(chunk.value, { stream: true });
        // SSE separates frames with blank lines (\n\n). The buffer can
        // legitimately contain multiple frames per chunk on a burst.
        let sep;
        // eslint-disable-next-line no-cond-assign
        while ((sep = buffer.indexOf("\n\n")) !== -1) {
          const rawFrame = buffer.slice(0, sep);
          buffer = buffer.slice(sep + 2);
          dispatch(parseFrame(rawFrame));
        }
      }
      // Server closed the stream cleanly; treat as transient and reconnect
      // (with the same backoff path as an error).
      if (!cancelled) {
        throw new Error("stream_closed");
      }
    } catch (err) {
      if (cancelled || controller.signal.aborted) return;
      if (typeof onError === "function") {
        try {
          onError(err);
        } catch {
          // ignore
        }
      }
      await sleep(retryDelayMs);
      retryDelayMs = Math.min(retryDelayMs * 2, maxRetryDelayMs);
      if (!cancelled) connect();
    }
  }

  connect();

  return function unsubscribe() {
    if (cancelled) return;
    cancelled = true;
    try {
      controller.abort();
    } catch {
      // ignore
    }
  };
}
